import {
  Box,
  Flex,
  Spacer,
  useDisclosure,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverTrigger,
  PopoverContent,
  Stack,
  FormControl,
  FormControlProps,
} from "@chakra-ui/react";
import { HorizontalGreyBlackField } from "components/DataDisplay/GreyBlackField";
import EmailRecipientTag from "components/Tag/ResourceTargetTag/EmailRecipientTag";
import { longAttributeStyles } from "components/DataDisplay/styles";
import { PlusCirlceButtonWithRef } from "components/Button/PlusCircleButton";
import { UseFormReturn, useFieldArray } from "react-hook-form";
import NewRecipientForm from "features/Resources/Email/Components/Form/NewRecipientForm";
import { EmailRecipient } from "features/Resources/Email/models/EmailRecipient";
import { get } from "lodash";
import ErrorText from "components/FormElements/ErrorText";

export function ShowEmailRecipients({ recipients, label, ...rest }: { recipients: EmailRecipient[]; label: string }) {
  return (
    <HorizontalGreyBlackField label={label} labelContainerProps={longAttributeStyles.labelContainerProps}>
      <Flex height="100%" flexWrap="wrap" alignItems="center" gap={2} {...rest}>
        {recipients.map((recipient, index) => (
          <EmailRecipientTag key={`${label}-recipient-${index}`} recipient={recipient} disabled />
        ))}
      </Flex>
    </HorizontalGreyBlackField>
  );
}

interface EmailRecipientsFieldProps {
  form: UseFormReturn<any>;
  label: string;
  fieldName: string;
  includeRelationTarget: boolean;
  includeAdd?: boolean;
  additionalButtons?: React.ReactNode;
  disabled?: boolean;
  includeErrors?: boolean;
  formControlProps?: FormControlProps;
  /** "boxed" (default) wraps the chips in the labeled grey field used in legacy detail views.
   *  "inline" renders just the chips + add button — use inside ComposeBox header rows. */
  variant?: "boxed" | "inline";
}

export default function EmailRecipientsField({
  form,
  label,
  fieldName,
  additionalButtons,
  includeRelationTarget,
  disabled = false,
  includeAdd = true,
  includeErrors = false,
  formControlProps = {},
  variant = "boxed",
}: EmailRecipientsFieldProps) {
  const {
    control,
    formState: { errors },
  } = form;
  const { fields, append, remove } = useFieldArray({
    control,
    name: fieldName,
    keyName: "fieldId",
  });
  const { isOpen, onOpen, onClose, getDisclosureProps } = useDisclosure();

  const disclosureProps = getDisclosureProps();

  const recipients = (fields || []).map((recipient, index) => (
    <EmailRecipientTag
      key={recipient.fieldId}
      recipient={recipient as unknown as EmailRecipient}
      remove={() => remove(index)}
      disabled={disabled}
    />
  ));

  const error = includeErrors ? get(errors, fieldName) : undefined;

  const hasRecipients = (fields || []).length > 0;
  const addLabel = includeRelationTarget
    ? `Add ${label.toLowerCase()} or role…`
    : `${hasRecipients ? "Add another" : "Add"} ${label.toLowerCase()}…`;

  const inlineAddTrigger = (
    <PopoverTrigger>
      <Box
        as="button"
        type="button"
        fontSize="sm"
        color="fg.subtle"
        bg="transparent"
        border="none"
        cursor="pointer"
        _hover={{ color: "fg.muted" }}
      >
        {addLabel}
      </Box>
    </PopoverTrigger>
  );

  const popoverContent = (
    <PopoverContent
      pt="3"
      bg="var(--chakra-colors-chakra-body-bg)"
      minW={{ base: "100%", lg: "max-content" }}
      width="100%"
      maxWidth="350px"
      display={isOpen ? "block" : "none"}
    >
      <PopoverCloseButton size="xs" sx={{ _hover: { bg: "transparent", color: "fg.emphasized" } }} />
      <PopoverBody>
        <NewRecipientForm
          onSubmit={(data: any) => {
            append(data);
            onClose();
          }}
          includeRelationTarget={includeRelationTarget}
        />
      </PopoverBody>
    </PopoverContent>
  );

  if (variant === "inline") {
    return (
      <Popover matchWidth closeOnBlur={false} {...disclosureProps} onOpen={onOpen} onClose={onClose}>
        <FormControl variant="unstyled" id={fieldName} isInvalid={!!error} isDisabled={disabled} {...formControlProps}>
          <Stack spacing={1}>
            <Flex gap={1.5} align="center" flexWrap="wrap">
              {recipients}
              {includeAdd && !disabled && inlineAddTrigger}
              {additionalButtons && (
                <>
                  <Spacer />
                  {additionalButtons}
                </>
              )}
            </Flex>
            <ErrorText error={error} />
          </Stack>
        </FormControl>
        {popoverContent}
      </Popover>
    );
  }

  return (
    <HorizontalGreyBlackField label={label} labelContainerProps={longAttributeStyles.labelContainerProps}>
      <Popover matchWidth closeOnBlur={false} {...disclosureProps} onOpen={onOpen} onClose={onClose}>
        <FormControl variant="unstyled" id={fieldName} isInvalid={!!error} isDisabled={disabled} {...formControlProps}>
          <Stack>
            <Flex gap="1" align="center" flexWrap="wrap">
              {recipients}
              {includeAdd && !disabled && (
                <PopoverTrigger>
                  <PlusCirlceButtonWithRef label={`Add ${label}`} />
                </PopoverTrigger>
              )}
              {additionalButtons}
            </Flex>
            <ErrorText error={error} />
          </Stack>
        </FormControl>
        {popoverContent}
      </Popover>
    </HorizontalGreyBlackField>
  );
}
